import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  useTheme,
  IconButton,
  Tooltip,
  Skeleton,
  TextField,
  Alert,
} from '@mui/material';
import { Edit, Add, Delete, Close, Save } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { useInjectReducer, useInjectSaga } from 'utils/redux-injectors';
import { selectDealerSelected } from 'app/pages/Header/selectors';
import { headerSaga } from 'app/pages/Header/saga';
import { reducer, sliceKey } from 'app/pages/Header/slice';
import ModalMAIQ from 'app/components/ModalMAIQ';
import AddIcon from '@mui/icons-material/Add';
import DataGridMAIQ from 'app/components/DataGridMAIQ';
import DataGridTypes from 'types/DataGridTypes';
import DealerSpecialsDetailsModal from './DealerSpecialsDetailsModal';
import DealerSpecialsService from 'app/apiService/dealerSpecials/dealerSpecialsApiService';
import ConfirmationModal from 'app/components/ConfirmationModal';
import SingleDatePickerMAIQ from 'app/components/SingleDatePickerMAIQ';

// Modal for Add/Edit Dealer Special
const DealerSpecialsModal = ({ open, onClose, initialData, onSave, isSubmitting }: any) => {
  const theme = useTheme();
  const [form, setForm] = useState({
    special_headline: '',
    special_description: '',
    start_date: '',
    end_date: '',
    applicable_model: '',
    disclaimer_text: '',
  });
  const [error, setError] = useState<string>('');

  useEffect(() => {
    if (initialData) {
      setForm({
        special_headline: initialData.special_headline || '',
        special_description: initialData.special_description || '',
        start_date: initialData.start_date ? initialData.start_date.split('T')[0] : '',
        end_date: initialData.end_date ? initialData.end_date.split('T')[0] : '',
        applicable_model: initialData.applicable_model || '',
        disclaimer_text: initialData.disclaimer_text || '',
      });
    } else {
      setForm({
        special_headline: '',
        special_description: '',
        start_date: '',
        end_date: '',
        applicable_model: '',
        disclaimer_text: '',
      });
    }
    setError('');
  }, [initialData, open]);

  const handleChange = (field: string, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const validateForm = () => {
    if (!form.special_headline?.trim()) {
      setError('Special headline is required');
      return false;
    }
    if (!form.special_description?.trim()) {
      setError('Special description is required');
      return false;
    }
    if (!form.start_date?.trim()) {
      setError('Start date is required');
      return false;
    }
    if (!form.end_date?.trim()) {
      setError('End date is required');
      return false;
    }
    if (!form.applicable_model?.trim()) {
      setError('Applicable model is required');
      return false;
    }
    if (new Date(form.start_date) >= new Date(form.end_date)) {
      setError('End date must be after start date');
      return false;
    }
    return true;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    onSave(form);
  };

  return (
    <ModalMAIQ open={open} onClose={onClose} hideDefaultCloseButton={true}>
      <Paper
        elevation={0}
        sx={{
          width: 600,
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 0,
        }}
      >
        {/* Header - fixed */}
        <Box
          sx={{
            p: 2,
            backgroundColor: theme.palette.secondary.main,
            color: 'white',
            zIndex: 10,
            flexShrink: 0,
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            width="100%"
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AddIcon fontSize="small" />
              <Typography variant="h6" fontWeight="600">
                {initialData ? 'Edit Dealer Special' : 'Add New Dealer Special'}
              </Typography>
            </Box>
            <Box>
              <Tooltip title="Close">
                <IconButton
                  aria-label="close"
                  onClick={onClose}
                  sx={{
                    color: 'white',
                    p: 0.5,
                  }}
                  size="small"
                >
                  <Close fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mx: 2, mt: 2 }}>
            {error}
          </Alert>
        )}

        {/* Scrollable content area */}
        <Box
          component="form"
          id="dealer-specials-form"
          onSubmit={handleSubmit}
          sx={{
            flex: 1,
            overflowY: 'auto',
            position: 'relative',
            py: 2,
            px: 3
          }}
        >
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
              Special Headline *
            </Typography>
            <TextField
              fullWidth
              value={form.special_headline}
              onChange={e => handleChange('special_headline', e.target.value)}
              required
              disabled={isSubmitting}
              variant="outlined"
              size="small"
              placeholder="Enter special headline"
              inputProps={{
                sx: { py: 1.5 }
              }}
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
              Special Description *
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              value={form.special_description}
              onChange={e => handleChange('special_description', e.target.value)}
              required
              disabled={isSubmitting}
              variant="outlined"
              size="small"
              placeholder="Enter special description"
              inputProps={{
                sx: { py: 1.5 }
              }}
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
              Start Date *
            </Typography>
            <SingleDatePickerMAIQ
              value={form.start_date}
              onChange={(date) => handleChange('start_date', date || '')}
              placeholder="Select start date"
              disabled={isSubmitting}
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
              End Date *
            </Typography>
            <SingleDatePickerMAIQ
              value={form.end_date}
              onChange={(date) => handleChange('end_date', date || '')}
              placeholder="Select end date"
              minDate={form.start_date ? new Date(form.start_date) : undefined}
              disabled={isSubmitting}
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
              Applicable Model *
            </Typography>
            <TextField
              fullWidth
              value={form.applicable_model}
              onChange={e => handleChange('applicable_model', e.target.value)}
              required
              disabled={isSubmitting}
              variant="outlined"
              size="small"
              placeholder="Enter applicable model"
              inputProps={{
                sx: { py: 1.5 }
              }}
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
              Disclaimer Text
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={2}
              value={form.disclaimer_text}
              onChange={e => handleChange('disclaimer_text', e.target.value)}
              disabled={isSubmitting}
              variant="outlined"
              size="small"
              placeholder="Enter disclaimer text"
              inputProps={{
                sx: { py: 1.5 }
              }}
            />
          </Box>
        </Box>

        {/* Footer action bar */}
        <Box 
          sx={{ 
            p: 2, 
            display: 'flex', 
            justifyContent: 'flex-end',
            backgroundColor: 'white',
            borderTop: '1px solid rgba(0, 0, 0, 0.12)',
            zIndex: 9,
            flexShrink: 0,
            gap: 2
          }}
        >
          <Button
            variant="outlined"
            onClick={onClose}
            disabled={isSubmitting}
            sx={{ 
              textTransform: 'none', 
              fontWeight: 600,
              color: 'text.secondary',
              borderColor: 'divider',
              py: 0.75
            }}
          >
            Cancel
          </Button>
          {isSubmitting ? (
            <Skeleton
              variant="rectangular"
              width={140}
              height={36}
              sx={{
                borderRadius: 1,
                bgcolor: 'rgba(15, 40, 76, 0.1)',
              }}
            />
          ) : (
            <Button
              variant="contained"
              type="submit"
              form="dealer-specials-form"
              onClick={(e) => {
                // If form submission doesn't happen through the form's onSubmit,
                // trigger it manually here as a fallback
                handleSubmit(e as any);
              }}
              startIcon={<Save />}
              sx={{ 
                textTransform: 'none', 
                fontWeight: 600, 
                bgcolor: theme.palette.secondary.main,
                color: 'white',
                py: 0.75,
                px: 3,
                '&:hover': {
                  bgcolor: '#0a1f3a',
                }
              }}
            >
              {initialData ? 'Update Special' : 'Create Special'}
            </Button>
          )}
        </Box>
      </Paper>
    </ModalMAIQ>
  );
};

const DealerSpecialsTab = () => {
  const theme = useTheme();
  const selectedDealer = useSelector(selectDealerSelected) || {};
  const selectedDealerId = selectedDealer.dealer || '';

  // Check if dealer is selected - if not, show message
  if (!selectedDealerId) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '50vh',
          color: 'text.secondary',
          fontSize: '1.5rem',
          fontWeight: 500,
        }}
      >
        <Typography variant="h4" gutterBottom component="div">
          Please select a dealer to view dealer specials
        </Typography>
      </Box>
    );
  }

  const [specials, setSpecials] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editData, setEditData] = useState<any>(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedSpecial, setSelectedSpecial] = useState<any>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteSpecialId, setDeleteSpecialId] = useState<string>('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedRowToDelete, setSelectedRowToDelete] = useState<any>(null);
  const [gridDeleteConfirm, setGridDeleteConfirm] = useState(false);

  useInjectReducer({ key: sliceKey, reducer: reducer });
  useInjectSaga({ key: sliceKey, saga: headerSaga });

  const handleAdd = () => {
    setEditData(null);
    setModalOpen(true);
  };

  const handleEdit = (row: any) => {
    setEditData(row);
    setModalOpen(true);
  };

  const handleViewDetails = (row: any) => {
    setSelectedSpecial(row);
    setDetailModalOpen(true);
  };

  const handleDelete = async (row: any) => {
    setSelectedRowToDelete(row);
    setGridDeleteConfirm(true);
  };

  const confirmGridDelete = async () => {
    if (!selectedRowToDelete) return;
    setIsDeleting(true);
    try {
      await DealerSpecialsService.deleteDealerSpecial(selectedRowToDelete._id);
      setRefreshKey(prev => prev + 1);
      setGridDeleteConfirm(false);
      setSelectedRowToDelete(null);
    } catch (err) {
      console.error('Delete dealer special error', err);
    } finally {
      setIsDeleting(false);
    }
  };

  const saveSpecial = async (id: string | null, payload: any) => {
    try {
      if (id) {
        await DealerSpecialsService.editDealerSpecial(id, payload);
      } else {
        await DealerSpecialsService.addDealerSpecial(payload);
      }
      setRefreshKey(prev => prev + 1);
    } catch (err) {
      console.error('Save dealer special error', err);
    }
  };

  const handleSave = async (data: any) => {
    setIsSubmitting(true);
    try {
      const payload = { ...data, dealership_id: selectedDealerId };
      await saveSpecial(editData?._id || null, payload);
      setModalOpen(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCellClick = (cell: any, clickedElement?: string) => {
    if (cell.field === 'actions') {
      // Handle action buttons (edit, delete)
      if (clickedElement === 'delete') {
        handleDelete(cell.row);
      } else if (clickedElement === 'edit') {
        handleEdit(cell.row);
      }
    } else if (cell.field === 'special_headline') {
      // Handle special headline click - open detail modal
      handleViewDetails(cell.row);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box>
            <Typography variant="h6" sx={{ color: '#0f284c', fontWeight: 600 }}>
              Dealer Specials
            </Typography>
            <Typography variant="body2" sx={{ color: '#6c757d' }}>
              Manage your dealer specials and promotional offers
            </Typography>
          </Box>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<AddIcon />}
            onClick={handleAdd}
            sx={{
              borderRadius: 1,
              textTransform: 'none',
              fontWeight: 600,
              px: 2.5,
              py: 1,
              backgroundColor: theme.palette.secondary.main,
              '&:hover': {
                backgroundColor: '#0a1f3a',
                boxShadow: '0 4px 10px rgba(15, 40, 76, 0.2)',
              },
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
          >
            Add New
          </Button>
        </Box>
        <DataGridMAIQ
          type={DataGridTypes.DEALER_SPECIALS(selectedDealerId)}
          onCellClick={handleCellClick}
          key={refreshKey}
        />
      </Paper>
      <DealerSpecialsModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        initialData={editData}
        onSave={handleSave}
        isSubmitting={isSubmitting}
      />
      <DealerSpecialsDetailsModal
        open={detailModalOpen}
        onClose={() => setDetailModalOpen(false)}
        data={selectedSpecial}
        onSave={async payload => {
          await saveSpecial(selectedSpecial?._id, { ...payload, dealership_id: selectedDealerId });
          setDetailModalOpen(false);
          setRefreshKey(prev => prev + 1);
        }}
        onDelete={async () => {
          try {
            await DealerSpecialsService.deleteDealerSpecial(selectedSpecial._id);
            setDetailModalOpen(false);
            setRefreshKey(prev => prev + 1);
          } catch (err) {
            console.error('Delete dealer special error', err);
          }
        }}
      />
      <ConfirmationModal
        open={gridDeleteConfirm}
        onClose={() => setGridDeleteConfirm(false)}
        onConfirm={confirmGridDelete}
        message="Are you sure you want to delete this dealer special?"
        loading={isDeleting}
      />
    </Box>
  );
};

export default DealerSpecialsTab;
